/* BEGIN_COPYRIGHT_HEADER

Copyright Vspry International Limited (c) 2020
All rights reserved.

END_COPYRIGHT_HEADER */

import { Address } from 'utils/formatters'
import { enumify, plutoFormMutation, plutoMutation, plutoQuery, plutoQueryQuiet, stringify } from './gqlInterface'
import { Activity, Customer, Goal } from './merchant'
import { mutationResponseString, PaginatedResponse, Pagination, paginationString } from './types'
import { RedemptionStatus } from './reward'

export type Store = {
    id: string
    name: string
    phone?: string
    bookingURL?: string
    address: Address
    endDate?: string
}

type OfferStatisticContact = {
    name: string
}

export type Offer = {
    id: string
    created: string
    campaignID: string
    title: string
    description: string
    expiryDate: string
    startDate?: string
    activationLastDate?: string
    activationLastDatePeriod?: number
    stores: Store[]
    bookingURL?: string
    maxActivations?: number
    audience?: number
    productID?: string
    productName?: string
    imageURL?: string
    type: 'product' | 'platform'
    url?: string
    urlLabel?: string
    statistics?: {
        prospects: number
        clicks: OfferStatisticContact[]
        activations: OfferStatisticContact[]
        redemptions: OfferStatisticContact[]
    }
    excludedTags?: string[]
    excludedSegments?: string[]
    excludedCustom?: string[]
    excludedContacts?: Customer[]
    includedTags?: string[]
    includedSegments?: string[]
    includedCustom?: string[]
    includedContacts?: Customer[]
    customMessageSubject?: string
    customMessageBody?: string
    customSMSMessage?: string
    offerCancelled?: boolean
}

export type OfferActivation = {
    id: string
    offerID: string
    status: 'Active' | 'Consumed' | 'Expired'
    activatedDate: string
    consumedDate?: string
    contactName: string
    offer: Offer
    type: 'product' | 'platform'
}

export type Subscription = {
    id: string
    status: string
    plan: string
    planName: string
    featuresEnabled: string[]
    start: string
    frequency: string
    nextCharge: string
    maxLocations: number
    maxCards: number
}

export type RewardItem = {
    id: string
    quantity: number
    name: string
    description?: string
    image?: string
}

export type Holding = {
    id: string
    name?: string
    product: string
    productId: string
    currencyMultiplier: number
    currencySymbol?: string
    asset: string
    balance: number
    paymentReference?: string
    planTotal?: number
    saleValue?: number
    initialDeposit?: number
    planVariation?: number
    targetDate?: string
    closed: boolean
    closureReason?: string
    closureDate?: string
    cardNumber?: number
    cardColor?: string
    cardBackground?: string
    mandates: {
        id: string
    }[]
    rewardsDetails?: {
        max: number
        pointsPerStamp: number
        displayCardName: boolean
        activities: Activity[]
        goals: Goal[]
    }
    bookingURL?: string
    bookingPhone?: string
    offers?: Offer[]
    layoutPriority: number
    issuerLogo?: string
    issuerPrimaryColor?: string
    issuerTextColor?: string
}
export const isHolding = (a: unknown): a is Holding => {
    if (typeof a !== 'object' || a === null) return false
    if (!('id' in a && 'product' in a && 'asset' in a && 'balance' in a)) return false
    if (!a.id || !a.product || !a.asset || typeof a.balance !== 'number') return false
    return true
}

export type Field = {
    id: string
    name: string
    ref: string
    canMultiple: boolean
    canBarcode: boolean
    children?: string[]
}

export type Question = {
    id: string
    question: string
    fields: Field[]
    customFields: Field[]
    family: boolean
    grouped: boolean
    groupTitleField?: Field
}

export type Answer = {
    id: string
    fieldID: string
    answer: string
    parentID: string
    groupID: string
}

export type QuestionHash = {
    questionID: string
    groupID?: string
    hash: string
}

export type IDCard = {
    id: string
    asset: string
    assetID: string
    product: string
    productId: string
    balance: number
    cardColor: string
    cardBackground: string
    cardLogo: string
    questions: Question[]
    questionHashes: QuestionHash[]
    answers: Answer[]
    avatar?: string
    layoutPriority: number
    startDate: string
    endDate?: string
    email: string
    firstName: string
    active: boolean
    offers?: Offer[]
    issuerLogo?: string
    issuerPrimaryColor?: string
    issuerTextColor?: string
}
export const isIDCard = (a: unknown): a is IDCard => {
    if (typeof a !== 'object' || a === null) return false
    if (!('id' in a && 'product' in a && 'asset' in a && 'questions' in a && 'answers' in a)) return false
    if (!a.id || !a.product || !a.asset || !Array.isArray(a.questions) || !Array.isArray(a.answers)) return false
    return true
}

export type Account = {
    id: string
    accountName: string
    accountTradingName?: string
    accountType?: string
    entityType?: string
    reviewed: boolean
    role: string
    employees?: number
    signatory: boolean
    currentHealthFund?: string
    currentHealthType?: string
    businessNumber?: string
    businessWebsite?: string
    businessIndustry?: string
    bookingURL?: string
    bookingPhone?: string
    businessAddress?: Address
    subscriptions?: Subscription[]
    stores?: Store[]
    holdings: Holding[]
    foundationDate?: string
    industryName?: string
    idCards: IDCard[]
    identityImage?: string
    marketingSMSNotifyOn?: boolean
    primaryColor?: string
    textColor?: string
    force2FAOn?: boolean
    preferredLang?: string
}

export type PendingCheckin = {
    id: string
    date: string
    productID: string
}

// queries
export type Contact = {
    id: string
    title?: string
    firstName: string
    middleNames?: string
    lastName: string
    lastNameSecond?: string
    suffix?: string
    email: string
    emailSecondary?: string
    emailVerified: boolean
    dob?: string
    gender?: string
    civilStatus?: string
    dependents?: number
    phone?: string
    phoneSecondary?: string
    phoneVerified: boolean
    marketingOptOut: boolean
    emailNotificationsOptOut: boolean
    SMSNotificationsOptOut: boolean
    multifactorEnabled: boolean
    tsAndCs: boolean
    address?: Address
    addressPresent?: Address
    emergencyName?: string
    emergencyPhone?: string
    emergencyRelationship?: string
    accounts: Account[]
    offers: Offer[]
    pendingCheckins: PendingCheckin[]
}
export const getUserContact = async () => plutoQuery<Contact>`
    contact {
        id
        title
        firstName
        middleNames
        lastName
        lastNameSecond
        suffix
        email
        emailSecondary
        emailVerified
        dob
        gender
        civilStatus
        dependents
        phone
        phoneSecondary
        phoneVerified
        marketingOptOut
        emailNotificationsOptOut
        SMSNotificationsOptOut
        multifactorEnabled
        tsAndCs
        address {
            unit
            number
            streetName
            suburb
            city
            postCode
            state
            country
        }
        addressPresent {
            unit
            number
            streetName
            suburb
            city
            postCode
            state
            country
        }
        emergencyName
        emergencyPhone
        emergencyRelationship
        accounts {
            id
            accountName
            accountTradingName
            accountType
            entityType
            reviewed
            role
            employees
            signatory
            currentHealthFund
            currentHealthType
            businessNumber
            businessWebsite
            businessIndustry
            bookingURL
            bookingPhone
            marketingSMSNotifyOn
            force2FAOn
            unreadClaims
            dealCount
            unreadOffers
            foundationDate
            industryName
            identityImage
            primaryColor
            textColor
            preferredLang
            businessAddress {
                unit
                number
                streetName
                suburb
                city
                postCode
                state
                country
            }
            subscriptions {
                id
                status
                plan
                planName
                featuresEnabled
                start
                frequency
                nextCharge
                maxLocations
                maxCards
            }
            stores {
                id
                name
                bookingURL
                phone
                address {
                    unit
                    number
                    streetName
                    suburb
                    city
                    postCode
                    state
                    country
                }
                endDate
            }
            idCards {
                id
                asset
                assetID
                product
                productId
                cardColor
                cardBackground
                cardLogo
                questions {
                  id
                  question
                  fields {
                    id
                    name
                    ref
                    canMultiple
                    canBarcode
                    children
                  }
                  customFields {
                    id
                    name
                    ref
                    canMultiple
                    canBarcode
                    children
                  }
                  family
                  grouped
                  groupTitleField {
                    id
                    name
                  }
                }
                questionHashes {
                    questionID
                    groupID
                    hash
                }
                answers {
                  id
                  answer
                  fieldID
                  parentID
                  groupID
                }
                avatar
                layoutPriority
                startDate
                endDate
                email
                firstName
                active
                offers {
                    id
                    created
                    title
                    description
                    expiryDate
                    activationLastDate
                    activationLastDatePeriod
                    productName
                    imageURL
                    stores {
                        name
                        id
                    }
                    maxActivations
                    audience
                    type
                    url
                    urlLabel
                }
                issuerLogo
                issuerPrimaryColor
                issuerTextColor
            }
            holdings {
                id
                name
                product
                productId
                currencyMultiplier
                currencySymbol
                asset
                balance
                paymentReference
                planTotal
                saleValue
                initialDeposit
                planVariation
                targetDate
                closed
                closureReason
                closureDate
                cardColor
                cardBackground
                cardNumber
                layoutPriority
                issuerLogo
                issuerPrimaryColor
                issuerTextColor
                mandates {
                    id
                }
                rewardsDetails {
                    max
                    pointsPerStamp
                    displayCardName
                    activities {
                        id
                        type
                        title
                        description
                        message
                        receive
                        imgURL
                        externalLink
                        value
                        status
                        basis
                        priority
                    }
                    goals {
                        id
                        title
                        value
                        termsAndConditions
                        acknowledgementMessage
                        acknowledgementSMS
                        inventoryItems {
                            id
                            name
                            description
                            quantity
                            image
                            limit
                            redeemed
                        }
                    }
                }
                bookingURL
                bookingPhone
                offers {
                    id
                    created
                    title
                    description
                    expiryDate
                    activationLastDate
                    activationLastDatePeriod
                    productName
                    imageURL
                    stores {
                        name
                        id
                    }
                    maxActivations
                    audience
                    type
                    url
                    urlLabel
                }
            }
        }
        unreadMessages
        offers {
            id
            created
            title
            description
            expiryDate
            activationLastDate
            activationLastDatePeriod
            productName
            imageURL
            stores {
                name
                id
            }
            maxActivations
            audience
            type
            url
            urlLabel
        }
        pendingCheckins {
            id
            date
            productID
        }
    }
`

export type PublicIDCardAnswer = {
    field: string
    answer: string
    children?: PublicIDCardAnswer[]
    groupID?: string
}
export type PublicIDCardQuestion = {
    question: string
    answers: PublicIDCardAnswer[]
}
export type PublicIDCardLookupResponse = {
    verified: boolean
    questions: PublicIDCardQuestion[]
}

type PublicIDCardAnswerStringChildren = {
    field: string
    answer: string
    children?: string
    groupID?: string
}
type PublicIDCardQuestionAnswerStringChildren = {
    question: string
    answers: PublicIDCardAnswerStringChildren[]
}
type PublicIDCardLookupResponseQuestionAnswerStringChildren = {
    verified: boolean
    questions: PublicIDCardQuestionAnswerStringChildren[]
}

const stringChildrenToAnswerChildren = (children?: string): PublicIDCardAnswer[] | undefined => (children ? JSON.parse(children) : undefined)

export const publicIDCardLookup = async (token: string, hash: string): Promise<PublicIDCardLookupResponse | null> => {
    const res = await plutoQuery<PublicIDCardLookupResponseQuestionAnswerStringChildren>`
        publicIDCardLookup(token:"${token}", hash:"${hash}") {
            verified
            questions {
                question
                answers {
                    field
                    answer
                    children
                    groupID
                }
            }
        }
    `
    return res
        ? {
              ...res,
              questions: res.questions.map((q) => ({
                  ...q,
                  answers: q.answers.map((a) => ({ ...a, children: stringChildrenToAnswerChildren(a.children) })),
              })),
          }
        : res
}

type GetOfferActivationsResponse = {
    accounts: [{ offerActivations: PaginatedResponse<OfferActivation> }]
}
export const getOfferActivations = async (
    accountID: string,
    pagination: Pagination,
    statuses: string[],
    sort: string,
    cardID: string | null = null
) => {
    const res = await plutoQuery<GetOfferActivationsResponse>`
        contact {
            accounts(id:"${accountID}") {
                offerActivations(pagination: ${pagination}, status: ${enumify(statuses)}, sort: ${sort}, cardID: "${cardID}") {
                    set {
                        id
                        status
                        activatedDate
                        consumedDate
                        contactName
                        offer {
                            id
                            created
                            title
                            description
                            activationLastDate
                            activationLastDatePeriod
                            stores {
                                id
                                name
                                bookingURL
                            }
                            bookingURL
                        }
                        type
                    }
                    ${paginationString}
                }
            }
        }
    `
    return res?.accounts[0]?.offerActivations ?? null
}

type GetOfferActivationResponse = {
    accounts: [{ offerActivation: OfferActivation }]
}
export const getOfferActivation = async (accountID: string, activationID: string) => {
    const res = await plutoQuery<GetOfferActivationResponse>`
        contact {
            accounts(id:"${accountID}") {
                offerActivation(id:"${activationID}") {
                    id
                    status
                    activatedDate
                    consumedDate
                    contactName
                    offer {
                        title
                        created
                        description
                        activationLastDate
                        activationLastDatePeriod
                    }
                    type
                }
            }
        }
    `
    return res?.accounts[0]?.offerActivation ?? null
}

export type AccountContact = {
    id: string
    email: string
    phone?: string
    role: string
    firstName: string
    lastName: string
    storeID?: string
    relationship?: string
    signatory: boolean
}
type GetAccountContactsResponse = { accounts: [{ contacts: AccountContact[] }] }
export const getAccountContacts = async (accountID: string) => {
    const res = await plutoQuery<GetAccountContactsResponse>`
        contact {
            accounts(id:"${accountID}") {
                contacts {
                    id
                    email
                    phone
                    role
                    firstName
                    lastName
                    storeID
                    relationship
                    signatory
                }
            }
        }
    `
    return res?.accounts[0]?.contacts ?? null
}

export type Transaction = {
    id: string
    type: string
    subtype?: string
    amount: number
    balance: number
    date: string
    requestID?: string
    otherAccount?: string
    claimName?: string
    goalName?: string
    reverseTxID?: string
    invoiceReference?: string
    invoiceURL?: string
    reversible: boolean
    reversedAmount?: number
}
type GetHoldingTransactionsResponse = {
    accounts: [{ holdings: [{ transactions: PaginatedResponse<Transaction> }] }]
}
export const getHoldingTransactions = async (accountID: string, holdingID: string, pagination: Pagination) => {
    const res = await plutoQuery<GetHoldingTransactionsResponse>`
        contact {
            accounts(id:"${accountID}") {
                holdings(id:"${holdingID}") {
                    transactions(pagination:${pagination}) {
                        set {
                            id
                            type
                            subtype
                            amount
                            balance
                            date
                            requestID
                            otherAccount
                            claimName
                            goalName
                            reverseTxID
                            invoiceReference
                            invoiceURL
                            reversible
                            reversedAmount
                        }
                        pagination {
                            nextPage
                            prevPage
                            total
                        }
                    }
                }
            }
        }`
    return res?.accounts[0]?.holdings[0]?.transactions ?? null
}

export type Redemption = {
    id: string
    date: Date
    goal: string
    costPerItem: number
    terms: string
    contact: string
    value: number
    status: RedemptionStatus
    reason?: string
    merchant: string
    items: (Omit<RewardItem, 'id'> & { itemID: string })[]
}
type GetAwardTransactionsResponse = {
    accounts: [{ holdings: [{ redemptions: PaginatedResponse<Redemption> }] }]
}
export const getAwardTransactions = async (accountID: string, holdingID: string, pagination: Pagination, statuses: string[], sort: string) => {
    const res = await plutoQuery<GetAwardTransactionsResponse>`
        contact {
            accounts(id:"${accountID}") {
                holdings(id:"${holdingID}") {
                    redemptions(pagination:${pagination}, status: ${enumify(statuses)}, sort: ${sort}) {
                        set {
                            id
                            goal
                            costPerItem
                            terms
                            date
                            contact
                            value
                            status
                            reason
                            merchant
                            items {
                                itemID
                                quantity
                                name
                                description
                                image
                            }
                        }
                        pagination {
                            nextPage
                            prevPage
                            total
                        }
                    }
                 }
            }
        }`
    return res?.accounts[0]?.holdings[0]?.redemptions ?? null
}

export type Message = {
    id: string
    title: string
    date: string
    message: string
    receiver: string
    sender?: string
    read: boolean
}
type GetUserMessageResponse = {
    message: Message
}
export const getUserMessage = async (messageID: string) => {
    const res = await plutoQuery<GetUserMessageResponse>`
        contact {
            message(id:"${messageID}") {
                id
                title
                date
                message
                receiver
                sender
                read
            }
        }
    `
    return res?.message ?? null
}

type GetUserMessagesResponse = {
    messages: PaginatedResponse<Omit<Message, 'status'>>
}
export const getUserMessages = async (pagination: Pagination, search: string) => {
    const res = await plutoQuery<GetUserMessagesResponse>`
        contact {
            messages(pagination: ${pagination}, search: "${search}") {
                set {
                    id
                    title
                    date
                    message
                    receiver
                    sender
                    read
                }
                pagination {
                    nextPage
                    prevPage
                    total
                    pageNavigation {
                        page
                        items
                        cursor
                    }
                }
            }
        }
    `
    return res?.messages ?? null
}

type GetUserUnreadMessagesResponse = { unreadMessages: number }
export const getUserUnreadMessages = async () => {
    const res = await plutoQueryQuiet<GetUserUnreadMessagesResponse>`
        contact {
            unreadMessages
        }
    `
    return res?.unreadMessages ?? 0
}

export const getActionContract = async (actionID: string) => plutoQuery<string>`actionContract(actionID:"${actionID}")`

type DocumentFile = {
    name: string
    url?: string
    size: number
    id: string
}
type Document = {
    id: string
    size: number
    date: string
    classification?: string
    files: DocumentFile[]
}
type GetUserDocumentsResponse = PaginatedResponse<Omit<Document, 'files'>>
export const getUserDocuments = async (pagination: Pagination) =>
    plutoQuery<GetUserDocumentsResponse>`
        documents(pagination: ${pagination}) {
            set {
                id
                size
                date
                classification
            }
            pagination {
                nextPage
                prevPage
                total
                pageNavigation {
                    page
                    items
                    cursor
                }
            }
        }
    `

export const getDocumentFiles = async (documentID: string) => plutoQuery<DocumentFile[]>`
        files(documentID:"${documentID}") {
            id
            name
            size
            url
        }
`

type Product = {
    id: string
    name: string
    businessName: string
    issuerLogo?: string
    issuerPrimaryColor?: string
    issuerTextColor?: string
    checkinOn: boolean
    checkoutOn: boolean
    checkoutEstimateOn: boolean
}
export const getProduct = async (productID: string) => plutoQuery<Product>`
    product(id:"${productID}") {
        id
        name
        businessName
        issuerLogo
        issuerPrimaryColor
        issuerTextColor
        checkinOn
        checkoutOn
        checkoutEstimateOn
    }
`

export const getProductByAsset = async (asset: string) => plutoQuery<Product>`
    productByAsset(asset:"${asset}") {
        id
        name
        businessName
        issuerLogo
        issuerPrimaryColor
        issuerTextColor
        checkinOn
        checkoutOn
        checkoutEstimateOn
    }
`

type Statement = {
    id: string
    name: string
    url: string
}
type GetUserStatementsResponse = {
    accounts: [{ statements: PaginatedResponse<Statement> }]
}
export const getUserStatements = async (accountID: string, pagination: Pagination) => {
    const res = await plutoQuery<GetUserStatementsResponse>`
        contact {
            accounts(id:"${accountID}") {
                statements(pagination: ${pagination}) {
                    set {
                        id
                        name
                        url
                    }
                    pagination {
                        nextPage
                        prevPage
                        total
                        pageNavigation {
                            page
                            items
                            cursor
                        }
                    }
                }
            }
        }
    `
    return res?.accounts[0]?.statements ?? null
}

export type FinStat = {
    id: string
    year: number
    taxCollected: boolean
    statURL: string
    taxURL: string
}
type GetUserFinancialStatementsResponse = {
    accounts: [{ financialStatements: FinStat[] }]
}
export const getUserFinancialStatements = async (accountID: string) => {
    const res = await plutoQuery<GetUserFinancialStatementsResponse>`
        contact {
            accounts(id:"${accountID}") {
                financialStatements {
                    id
                    year
                    taxCollected
                    statURL
                    taxURL
                }
            }
        }
    `

    return res?.accounts[0].financialStatements
}

export type BusinessDocument = {
    id: string
    name?: string
    classification: string
    otherClassification?: string
    url: string
    uploaded: string
}
type GetUserBusinessDocumentsResposne = {
    accounts: [{ businessDocuments: BusinessDocument[] }]
}
export const getUserBusinessDocuments = async (accountID: string, classifications: string[]) => {
    const res = await plutoQuery<GetUserBusinessDocumentsResposne>`
        contact {
            accounts(id:"${accountID}") {
                businessDocuments(classifications:${classifications.map((c) => `"${c}"`)}) {
                    id
                    name
                    classification
                    otherClassification
                    url
                    uploaded
                }
            }
        }
    `

    return res?.accounts[0].businessDocuments
}

export type Entity = { id: string; name: string }
type GetUserPreviousLenderResponse = {
    accounts: [{ prevLender: Entity | null }]
}
export const getUserPreviousLender = async (accountID: string) => {
    const res = await plutoQuery<GetUserPreviousLenderResponse>`
        contact {
            accounts(id:"${accountID}") {
                prevLender {
                    id
                    name
                }
            }
        }
    `

    return res?.accounts[0]?.prevLender ?? null
}

// mutations
type SubmitSupportTicketInput = {
    contactID?: string
    accountID?: string
    holdingID?: string
    description?: string
}
export const submitSupportTicket = async (input: SubmitSupportTicketInput) => plutoMutation`
    submitSupportTicket(input: ${input}) ${mutationResponseString}
`

type CreateActivityInput = {
    accountID: string
    holdingID?: string
    idCardID?: string
    to: string
    from: string
    type: string
}
export const createActivity = async (input: CreateActivityInput) => plutoMutation`
    createActivity(input: ${input}) ${mutationResponseString}
`

type UpdateUserInput = {
    title?: string
    firstName?: string
    middleNames?: string
    lastName?: string
    lastNameSecond?: string
    suffix?: string
    dob?: string
    gender?: string
    civilStatus?: string
    dependents?: number
    email?: string
    emailSecondary?: string
    phone?: string
    phoneSecondary?: string
    emergencyName?: string
    emergencyPhone?: string
    emergencyRelationship?: string
    address?: Address
    addressPresent?: Address
    marketingOptOut?: boolean
    emailNotificationsOptOut?: boolean
    SMSNotificationsOptOut?: boolean
    preferredLang?: string
}
export const updateUser = async (input: UpdateUserInput) => plutoMutation`
    updateUser(input: ${input}) ${mutationResponseString}
`

type UpdateAccountInput = {
    accountID: string
    currentHealthFund?: string
    currentHealthType?: string
}
export const updateAccount = async (input: UpdateAccountInput) => plutoMutation`
    updateAccount(input: ${input}) ${mutationResponseString}
`

type AddUserDeviceResponse = { success: boolean; message: string; deviceID?: string }
export const addUserDevice = async (notificationToken: string) => plutoMutation<AddUserDeviceResponse>`
    addUserDevice(input: ${{ token: notificationToken }}) { success message deviceID }
`

type SignContractInput = {
    actionID: string
    contactID: string
    accountID: string
    device: string
}
export const signContract = async (input: SignContractInput, file: File) => {
    const query = `mutation($file: Upload!) {
        signContract(input: ${stringify(input)}, file: $file) ${mutationResponseString}
    }`
    return plutoFormMutation(query, { file })
}

export const setMessageRead = async (actionID: string, read: boolean) =>
    plutoMutation`readMessage(input: ${{ actionID, read }}) ${mutationResponseString}`

type UploadDocumentInput = {
    contactID: string
    accountID: string
    fateID?: string
    actionID?: string
    dealID?: string
    classification: string
    classificationOther?: string
}
export const uploadDocument = async (input: UploadDocumentInput, files: File[]) => {
    const query = `mutation($files: [Upload!]!) {
        uploadDocument(input: ${stringify(input)}, files: $files) ${mutationResponseString}
    }`
    return plutoFormMutation(query, { files })
}

type CreateUserHoldingInput = {
    product: string
    account: string
    store?: string | null
    referrer?: string
    nickname?: string
    purchase?: number
    target?: string
    source?: string
    dynamicInputs?: Record<string, string | Record<string, string>>
}
export const createUserHolding = async (input: CreateUserHoldingInput, dynamicFiles: File[]) =>
    plutoFormMutation(
        `
            mutation($dynamicFiles: [Upload!]!) {
                addProduct(input: ${stringify(input)}, dynamicFiles: $dynamicFiles) ${mutationResponseString}
            }
        `,
        { dynamicFiles }
    )

export const recordUserLogin = async () => plutoMutation`recordLogin ${mutationResponseString}`

export const updateBankAccountStatus = async (bankAccountID: string, status: boolean) =>
    plutoMutation`updateBankAccount(input: ${{ id: bankAccountID, status }}) ${mutationResponseString}`

type UploadBankStatementInput = {
    accountID: string
    bankAccountID: string
    start: string
    end: string
}
export const uploadBankStatement = async (input: UploadBankStatementInput, file: File) =>
    plutoFormMutation(
        `
            mutation($file: Upload!) {
                uploadBankStatement(input: ${stringify(input)}, file: $file) ${mutationResponseString}
            }
        `,
        { file }
    )

type UploadTaxReturnInput = {
    accountID: string
    finStatID: string
}
export const uploadTaxReturn = async (input: UploadTaxReturnInput, file: File) =>
    plutoFormMutation(
        `
            mutation($file: Upload!) {
                uploadTaxReturn(input: ${stringify(input)}, file: $file) ${mutationResponseString}
            }
        `,
        { file }
    )

type UploadFinStatInput = {
    accountID: string
    year: string
}
export const uploadFinStat = async (input: UploadFinStatInput, stat: File, tax?: File) =>
    plutoFormMutation(
        `
            mutation($stat: Upload!, $tax: Upload) {
                uploadFinStat(input: ${stringify(input)}, stat: $stat, tax: $tax) ${mutationResponseString}
            }
        `,
        { stat, ...(tax ? { tax } : {}) }
    )

type UploadBusinessDocumentInput = {
    accountID: string
    classification: string
    otherClassification?: string
}
export const uploadBusinessDocument = async (input: UploadBusinessDocumentInput, file: File) =>
    plutoFormMutation(
        `
            mutation($file: Upload!) {
                uploadBusinessDocument(input: ${stringify(input)}, file: $file) ${mutationResponseString}
            }
        `,
        { file }
    )

type AddReferrerInput = {
    accountID: string
    referrerID: string
}
export const addReferrer = async (input: AddReferrerInput) => plutoMutation`addReferrer(input: ${input}) ${mutationResponseString}`

type UpdateHoldingLayoutPriorityInput = {
    accountID: string
    holdings: {
        holdingID: string
        priority: number
    }[]
}
export const updateHoldingLayoutPriority = async (input: UpdateHoldingLayoutPriorityInput) =>
    plutoMutation`updateHoldingLayoutPriority(input: ${input}) ${mutationResponseString}`

type UpdateIDCardLayoutPriorityInput = {
    accountID: string
    idCards: {
        idCardID: string
        priority: number
    }[]
}
export const updateIDCardLayoutPriority = async (input: UpdateIDCardLayoutPriorityInput) =>
    plutoMutation`updateIDCardLayoutPriority(input: ${input}) ${mutationResponseString}`

export const shorten = async (input: string) => plutoQuery<string>`shorten(input: "${input}")`

type CloseHoldingInput = {
    holdingID: string
    accountID: string
}
export const closeHolding = async (input: CloseHoldingInput) => plutoMutation`closeHolding(input: ${input}) ${mutationResponseString}`

export const getStoreName = async (storeID: string) => plutoQuery<string>`storeName(storeID: "${storeID}")`
